import React, { useEffect, useMemo, useRef, useState } from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useFlags } from "launchdarkly-react-client-sdk";

import { TASK_TITLE_MAX_LENGTH } from "@fartherfinance/frontend/api/Tasks/constants/common";
import useRemoveTaskV3 from "@fartherfinance/frontend/api/Tasks/hooks/useRemoveTask.v3";
import useUpdateTaskV3 from "@fartherfinance/frontend/api/Tasks/hooks/useUpdateTask.v3";
import { TaskDetailsV3 } from "@fartherfinance/frontend/api/Tasks/Types";

import { useTasksNavigation } from "../../../hooks";
import TaskMessageModal from "../../../TaskMessageModal/TaskMessageModal";
import { useTaskPermissions } from "../../../taskPermissionsContext";
import { isTaskFieldEditingRestricted } from "../../../utils";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import Menu from "@src/sharedComponents/Menu/Menu";
import Text from "@src/sharedComponents/Typography/Text";
import { extractAxiosErrorMessage } from "@src/utils/axios";

import styles from "./TaskTitle.module.css";

interface TaskTitleProps {
  taskDetails: TaskDetailsV3;
}
type ValidationResult = { valid: true } | { valid: false; message: string };

const TaskTitle: React.FC<TaskTitleProps> = ({ taskDetails }) => {
  const { enableTaskSubtasks } = useFlags();

  const [isSwitchToTaskModalVisible, setIsSwitchToTaskModalVisible] =
    useState(false);
  const [isDeleteTaskModalVisible, setIsDeleteTaskModalVisible] =
    useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [titleValue, setTitleValue] = useState(taskDetails.title);
  const [validationResult, setValidationResult] = useState<ValidationResult>({
    valid: true,
  });

  const auth = useAdvisorRequestAuth();
  const callUpdateTaskV3 = useUpdateTaskV3(auth);
  const callRemoveTaskV3 = useRemoveTaskV3(auth);
  const statusNotification = useStatusNotification();
  const { handleNavigateToTasksList } = useTasksNavigation();

  const { taskPermissions } = useTaskPermissions();
  const {
    isAllowedToDelete: isAllowedToDeleteTask,
    isAllowedToUpdateMetadata: isAllowedToEditTaskMetadata,
  } = taskPermissions(taskDetails);

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const div = contentRef.current;
    if (!div) {
      return;
    }

    const observer = new MutationObserver(() => {
      if (div.innerHTML === "<br>") {
        div.innerHTML = "";
      }
    });

    observer.observe(div, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  const handleDisableNewline = (
    event: React.KeyboardEvent<HTMLDivElement>
  ): void => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleUpdateTaskTitle = async (newTaskTitle: string): Promise<void> => {
    if (newTaskTitle === taskDetails.title) {
      return;
    }

    if (newTaskTitle.length > TASK_TITLE_MAX_LENGTH) {
      statusNotification("Failed to update task, title is too long.", "Error");
      setTitleValue(taskDetails.title);
    }

    if (
      newTaskTitle.length < 1 ||
      newTaskTitle === "\n" ||
      newTaskTitle === "" ||
      newTaskTitle === " "
    ) {
      statusNotification("Failed to update task, title is too short.", "Error");
      setTitleValue(taskDetails.title);
      return;
    }

    try {
      await callUpdateTaskV3({
        taskId: taskDetails.id,
        request: { title: newTaskTitle },
      });

      trackEvent({ name: "Advisor Tasks Update" });
    } catch (error) {
      setTitleValue(taskDetails.title);
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to update task."),
        "Error"
      );
    }
  };

  const handleTurnSubtaskIntoTask = async (): Promise<void> => {
    try {
      setIsMutating(true);

      await callUpdateTaskV3({
        taskId: taskDetails.id,
        // NOTE: this is used to invalidate cache
        parentTaskId: taskDetails.parent?.id,
        // NOTE: this sets the parent task to null
        request: { parentId: { value: null } },
      });

      setIsSwitchToTaskModalVisible(false);
      trackEvent({ name: "Advisor Tasks Update" });
      statusNotification("Task updated successfully.", "Success");
    } catch (error) {
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to update task."),
        "Error"
      );
    } finally {
      setIsMutating(false);
    }
  };

  const handleDeleteTask = async (): Promise<void> => {
    try {
      setIsMutating(true);

      await callRemoveTaskV3(taskDetails.id);

      setIsDeleteTaskModalVisible(false);
      trackEvent({ name: "Advisor Tasks Delete" });
      statusNotification("Task deleted successfully.", "Success");
    } catch (error) {
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to delete task."),
        "Error"
      );
    } finally {
      setIsMutating(false);
      handleNavigateToTasksList();
    }
  };

  const handleInputChange = (event: React.FormEvent<HTMLDivElement>): void => {
    const newTitle = event.currentTarget.innerText;
    if (newTitle.length === 0) {
      setValidationResult({
        valid: false,
        message: "Title cannot be empty",
      });
    } else if (newTitle.length <= TASK_TITLE_MAX_LENGTH) {
      setValidationResult({ valid: true });
    } else {
      setValidationResult({
        valid: false,
        message: "Choose a shorter title",
      });
    }
  };

  const taskMenuOptions = useMemo(
    () => [
      ...(enableTaskSubtasks && taskDetails.parent !== null
        ? [
            {
              label: "Switch to Task",
              isCloseOnClick: true,
              onClick: () => setIsSwitchToTaskModalVisible(true),
            },
          ]
        : []),
      {
        label: "Delete",
        className: styles.delete,
        isDisabled: !isAllowedToDeleteTask,
        isCloseOnClick: true,
        onClick: () => setIsDeleteTaskModalVisible(true),
      },
    ],
    [enableTaskSubtasks, isAllowedToDeleteTask, taskDetails]
  );

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.titleContainer}>
            <Text
              ref={contentRef}
              className={styles.title}
              variant="title2"
              contentEditable={
                isAllowedToEditTaskMetadata &&
                isTaskFieldEditingRestricted("title", taskDetails) === false
              }
              suppressContentEditableWarning={true}
              spellCheck={false}
              onKeyDown={(event) => handleDisableNewline(event)}
              onBlur={(event) =>
                validationResult.valid &&
                handleUpdateTaskTitle(event.currentTarget.innerText)
              }
              onInput={handleInputChange}
              placeholder="Task title..."
            >
              {titleValue}
            </Text>
          </div>
          <Menu options={taskMenuOptions}>
            {({ handleOpenMenu }) => (
              <IconButton
                onClick={handleOpenMenu}
                iconClassName={styles.menuIcon}
                IconComponent={MoreHorizIcon}
              />
            )}
          </Menu>
        </div>
        {!validationResult.valid && (
          <Text variant="body" className={styles.errorMessage}>
            {validationResult.message}
          </Text>
        )}
      </div>

      {enableTaskSubtasks && (
        <TaskMessageModal
          title="Switch To Task"
          isOpen={isSwitchToTaskModalVisible}
          onClose={{
            label: "Cancel",
            action: () => setIsSwitchToTaskModalVisible(false),
          }}
          onConfirm={{
            label: "Save",
            action: handleTurnSubtaskIntoTask,
            isDisabled: isMutating,
          }}
        >
          <Text variant="body" color="textSubtle">
            Are you sure you want to change the "{taskDetails.title}" subtask to
            a task?
          </Text>
        </TaskMessageModal>
      )}

      <TaskMessageModal
        title="Delete Task?"
        isOpen={isDeleteTaskModalVisible}
        onClose={{
          label: "Cancel",
          action: () => setIsDeleteTaskModalVisible(false),
        }}
        onConfirm={{
          label: "Delete",
          action: handleDeleteTask,
          isDisabled: isMutating,
        }}
      >
        <Text variant="body" color="textSubtle">
          By proceeding you will permanently delete the task "
          {taskDetails.title}".
        </Text>
      </TaskMessageModal>
    </>
  );
};

export default TaskTitle;
